import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  Paper,
  Alert,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { StatsAtGlance } from 'components/emailHistory/StatsAtGlance';
import { AIInsight } from 'components/emailHistory/AIInsight';
import { QuickFilters } from 'components/emailHistory/QuickFilters';
import { StatChip } from 'components/emailHistory/StatChip';
import { StatusChip } from 'components/emailHistory/StatusChip';
import { ExpandedRowBulkEmail } from 'components/emailHistory/ExpandedRowBulkEmail';
import { EmailCard } from 'components/emailHistory/EmailCard';
import { EmailCountBarChart } from 'components/emailHistory/charts';
import { useEmailHistoryFilters } from 'hooks/emailHistory/useEmailHistoryFilters';
import { useBulkEmails } from 'hooks/emailHistory/useBulkEmails';
import { useIsMobile } from 'hooks/useIsMobile';
import { isoToDateInput, dateRangeToISO, formatDate } from 'utils/emailHistory/dateUtils';
import { DEBOUNCE_DELAY, BULK_EMAIL_SORT_FIELDS } from 'constants/emailHistory';
import { ROUTE_EMAIL_SENDER } from 'constants/routerPath';

/**
 * Bulk email row component with inline expansion
 */
function BulkEmailRow({ bulkEmail, expandedRowIds, setExpandedRowIds, onNavigateToIndividualEmails }) {
  const isExpanded = expandedRowIds.includes(bulkEmail.emailId);

  const toggleExpand = () => {
    if (isExpanded) {
      setExpandedRowIds(expandedRowIds.filter(id => id !== bulkEmail.emailId));
    } else {
      setExpandedRowIds([...expandedRowIds, bulkEmail.emailId]);
    }
  };

  return (
    <React.Fragment>
      {/* Main Data Row */}
      <TableRow hover sx={{ cursor: 'pointer' }}>
        <TableCell>
          <IconButton
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
            size="small"
            onClick={toggleExpand}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <Typography variant="body1">{bulkEmail.subject}</Typography>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <Typography variant="body2">{bulkEmail.campaignName || 'N/A'}</Typography>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <Typography variant="body2">{formatDate(bulkEmail.sendDate)}</Typography>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatChip value={bulkEmail.attempted} />
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatChip value={bulkEmail.delivered} />
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatChip value={bulkEmail.uniqueOpens} />
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatChip value={bulkEmail.uniqueClicks} />
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatusChip status={bulkEmail.status} type="bulk" />
        </TableCell>
        <TableCell>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => onNavigateToIndividualEmails(bulkEmail.emailId)}
            endIcon={<ChevronRightIcon />}
            sx={{ minWidth: 100 }}
          >
            View
          </Button>
        </TableCell>
      </TableRow>

      {/* Expanded Details Row */}
      <TableRow>
        <TableCell sx={{ p: 0, borderBottom: isExpanded ? 1 : 0, bgcolor: 'white' }} colSpan={10}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 3, bgcolor: 'white' }}>
              <ExpandedRowBulkEmail
                emailId={bulkEmail.emailId}
                onNavigateToIndividualEmails={onNavigateToIndividualEmails}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

/**
 * @component BulkEmailsTab
 * @description Bulk emails tab showing all bulk email sends
 * @returns {JSX.Element}
 */
function BulkEmailsTab() {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { filters, setFilter, navigateToTab } = useEmailHistoryFilters();
  const { bulkEmails, totalRows, campaigns, stats, isLoading, error } = useBulkEmails(filters);
  
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const isMobile = useIsMobile();

  const [dateRange, setDateRange] = useState(
    filters.dateFrom || filters.dateTo
      ? { from: isoToDateInput(filters.dateFrom), to: isoToDateInput(filters.dateTo) }
      : null
  );

  // Initialize date range from URL params if present
  useEffect(() => {
    if (filters.dateFrom || filters.dateTo) {
      setDateRange({
        from: isoToDateInput(filters.dateFrom),
        to: isoToDateInput(filters.dateTo),
      });
    } else {
      setDateRange(null);
    }
  }, [filters.dateFrom, filters.dateTo]);

  // Sync searchInput with URL params when they change externally
  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // Debounced search effect - only update URL if search actually changed
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmedSearch = searchInput ? searchInput.trim() : '';
      const currentSearch = filters.search || '';
      
      // Only update if search actually changed
      if (trimmedSearch !== currentSearch) {
        setSearchParams((prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          if (trimmedSearch) {
            newParams.set('search', trimmedSearch);
          } else {
            newParams.delete('search');
          }
          newParams.set('page', '0');
          return newParams;
        });
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchInput, filters.search, setSearchParams]);

  // Handle date range change
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    const { dateFrom, dateTo } = dateRangeToISO(newRange);
    
    // Update both date filters and page in a single setSearchParams call to avoid race conditions
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      if (dateFrom) {
        newParams.set('dateFrom', dateFrom);
      } else {
        newParams.delete('dateFrom');
      }
      if (dateTo) {
        newParams.set('dateTo', dateTo);
      } else {
        newParams.delete('dateTo');
      }
      newParams.set('page', '0');
      return newParams;
    });
  };

  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setFilter('page', newPage);
    setExpandedRowIds([]); // Close expanded rows when changing pages
  };

  const handleChangeRowsPerPage = (event) => {
    setFilter('pageSize', parseInt(event.target.value, 10));
    setFilter('page', 0);
    setExpandedRowIds([]);
  };

  // Handle sorting
  const handleSort = (field) => {
    const isAsc = filters.sortBy === field && filters.sortOrder === 'asc';
    setFilter('sortBy', field);
    setFilter('sortOrder', isAsc ? 'desc' : 'asc');
  };

  // Navigate to individual emails tab with bulk email filter
  const handleNavigateToIndividualEmails = (bulkEmailId) => {
    navigateToTab('individual', { bulkEmailId });
  };

  // Navigate to email sender to send new email
  const handleSendNewEmail = () => {
    navigate(ROUTE_EMAIL_SENDER);
  };

  // Handle campaign filter change
  const handleCampaignChange = (campaignId) => {
    // campaignId comes as a string from Select, or null for "All"
    const parsedId = campaignId && campaignId !== '' ? parseInt(campaignId, 10) : null;
    
    // Update both campaignId and page in a single setSearchParams call to avoid race conditions
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      if (parsedId === null) {
        newParams.delete('campaignId');
      } else {
        newParams.set('campaignId', String(parsedId));
      }
      newParams.set('page', '0');
      return newParams;
    });
  };

  // Initialize sortBy to 'sendDate' if not set or invalid for bulk emails
  useEffect(() => {
    if (!filters.sortBy || !BULK_EMAIL_SORT_FIELDS.includes(filters.sortBy)) {
      setFilter('sortBy', 'sendDate');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Map UI field names to API field names for sorting
  const mapFieldToApiField = (field) => {
    const fieldMap = {
      'delivered': 'deliveryRate',
      'uniqueOpens': 'openRate',
      'uniqueClicks': 'clickRate',
    };
    return fieldMap[field] || field;
  };

  // Define sortable columns
  const columns = [
    { field: 'expand', label: '', sortable: false, width: '50px' },
    { field: 'subject', label: 'Subject', sortable: true, width: '200px' },
    { field: 'campaignName', label: 'Campaign', sortable: false, width: '160px' },
    { field: 'sendDate', label: 'Send Date', sortable: true, width: '140px' },
    { field: 'attempted', label: 'Sent', sortable: true, width: '90px' },
    { field: 'delivered', label: 'Delivered', sortable: true, width: '90px' },
    { field: 'uniqueOpens', label: 'Opens', sortable: true, width: '85px' },
    { field: 'uniqueClicks', label: 'Clicks', sortable: true, width: '85px' },
    { field: 'status', label: 'Status', sortable: false, width: '110px' },
    { field: 'actions', label: '', sortable: false, width: '110px' },
  ];

  // Prepare stats data
  const statsData = [
    { label: 'Total Bulk Sends', value: stats.totalBulkSends },
    { label: 'Individual Emails Sent', value: stats.individualEmailsSent },
    { label: 'Emails Delivered', value: stats.delivered },
    { label: 'Contacts Reached', value: stats.contactsInList },
  ];

  // Prepare campaign options for dropdown
  const campaignOptions = campaigns.map((campaign) => ({
    value: campaign.campaignId,
    label: campaign.campaignName,
  }));

  // Generate dynamic title based on filters
  const generateStatsTitle = () => {
    let title = "Viewing All Bulk Email Sends";
    const parts = [];

    // Add campaign filter if applied
    if (filters.campaignId) {
      const campaign = campaigns.find(c => c.campaignId === filters.campaignId);
      if (campaign) {
        parts.push(`for ${campaign.campaignName} Campaign`);
      }
    }

    // Add date filters
    if (filters.dateFrom && filters.dateTo) {
      parts.push(`Between ${formatDate(filters.dateFrom)} and ${formatDate(filters.dateTo)}`);
    } else if (filters.dateFrom) {
      parts.push(`from ${formatDate(filters.dateFrom)}`);
    } else if (filters.dateTo) {
      parts.push(`Up To ${formatDate(filters.dateTo)}`);
    }

    if (parts.length > 0) {
      title += ` ${parts.join(' ')}`;
    }

    return title;
  };

  const statsTitle = generateStatsTitle();
  const statsSubtitle = "A Bulk Email is a single email sent to all contacts in a campaign at the same time.";

  // Prepare bar chart data - count of bulk sends per date
  const barChartData = React.useMemo(() => {
    if (!bulkEmails || bulkEmails.length === 0) return [];
    
    return bulkEmails
      .filter(email => email.sendDate)
      .map(email => ({
        date: email.sendDate,
      }));
  }, [bulkEmails]);

  return (
    <Box>
      {/* Stats + AI Overview Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <StatsAtGlance stats={statsData} loading={isLoading} title={statsTitle} subtitle={statsSubtitle}>
            <EmailCountBarChart
              data={barChartData}
              loading={isLoading}
              height={200}
              yAxisLabel="Bulk Sends"
            />
          </StatsAtGlance>
        </Grid>
        <Grid item xs={12} md={4}>
          <AIInsight tab="bulk-emails" filters={filters} />
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Quick Filters and Data Table */}
      <Paper>
        <QuickFilters
          searchValue={searchInput}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search subject..."
          showDateRange={true}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          dateRangeDefault="all"
          startButton={
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleSendNewEmail}
              fullWidth={isMobile}
            >
              Send new email
            </Button>
          }
          additionalFilter={
            campaigns.length > 0
              ? {
                  label: 'Campaign',
                  value: filters.campaignId != null ? filters.campaignId : '',
                  options: campaignOptions,
                  onChange: handleCampaignChange,
                }
              : undefined
          }
        />
        {isMobile ? (
          <Box sx={{ p: 2 }}>
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : bulkEmails.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No bulk emails found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No bulk emails match your current filters. Try adjusting your date range.
                </Typography>
              </Box>
            ) : (
              <>
                {bulkEmails.map((bulkEmail) => {
                  const isExpanded = expandedRowIds.includes(bulkEmail.emailId);
                  return (
                    <EmailCard
                      key={bulkEmail.emailId}
                      data={{
                        subject: bulkEmail.subject,
                        campaignName: bulkEmail.campaignName || 'N/A',
                        sendDate: formatDate(bulkEmail.sendDate),
                        attempted: bulkEmail.attempted,
                        delivered: bulkEmail.delivered,
                        uniqueOpens: bulkEmail.uniqueOpens,
                        uniqueClicks: bulkEmail.uniqueClicks,
                        status: bulkEmail.status,
                      }}
                      type="bulk"
                      isExpanded={isExpanded}
                      onToggleExpand={() => {
                        if (isExpanded) {
                          setExpandedRowIds(expandedRowIds.filter(id => id !== bulkEmail.emailId));
                        } else {
                          setExpandedRowIds([...expandedRowIds, bulkEmail.emailId]);
                        }
                      }}
                      expandedContent={
                        isExpanded ? (
                          <ExpandedRowBulkEmail
                            emailId={bulkEmail.emailId}
                            onNavigateToIndividualEmails={handleNavigateToIndividualEmails}
                          />
                        ) : null
                      }
                      actionButton={
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          endIcon={<ChevronRightIcon />}
                          onClick={() => handleNavigateToIndividualEmails(bulkEmail.emailId)}
                        >
                          View
                        </Button>
                      }
                      onActionClick={() => handleNavigateToIndividualEmails(bulkEmail.emailId)}
                    />
                  );
                })}
                <TablePagination
                  component="div"
                  count={totalRows}
                  page={filters.page}
                  onPageChange={handleChangePage}
                  rowsPerPage={filters.pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[]}
                />
              </>
            )}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        sx={{ width: column.width, fontWeight: 600 }}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={filters.sortBy === mapFieldToApiField(column.field)}
                            direction={filters.sortBy === mapFieldToApiField(column.field) ? filters.sortOrder : 'asc'}
                            onClick={() => handleSort(mapFieldToApiField(column.field))}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : bulkEmails.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No bulk emails found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          No bulk emails match your current filters. Try adjusting your date range.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    // Server-side search is handled by the API endpoint
                    bulkEmails.map((bulkEmail) => (
                        <BulkEmailRow
                          key={bulkEmail.emailId}
                          bulkEmail={bulkEmail}
                          expandedRowIds={expandedRowIds}
                          setExpandedRowIds={setExpandedRowIds}
                          onNavigateToIndividualEmails={handleNavigateToIndividualEmails}
                        />
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRows}
              page={filters.page}
              onPageChange={handleChangePage}
              rowsPerPage={filters.pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

export default BulkEmailsTab;
